import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { from, of, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, finalize, tap } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { LinkGroupModalComponent } from 'src/app/shared/components/link-group-modal/link-group-modal.component';
import { ProgressionDialogData } from 'src/app/shared/components/progression-dialog/progression-dialog-data.model';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { TableLabelCellEnum } from 'src/app/shared/components/table/shared/models/table-label-cell.enum';
import { BasicService } from 'src/app/shared/services/basic.service';
import { FacebookService } from 'src/app/shared/strategies/channels';

import { FacebookLocationsService } from './facebook-locations.service';
import { FacebookLocationStatusEnum } from './shared/enums/facebook-location-status.enum';
import { FacebookLocation } from './shared/models';
import { FacebookLocationConnection } from './shared/models/facebook-location-connection.model';

@Component({
  selector: 'app-facebook-locations',
  templateUrl: './facebook-locations.component.html',
  styleUrls: ['./facebook-locations.component.scss'],
})
export class FacebookLocationsComponent implements OnInit, OnDestroy {
  facebookSitesChoices: Choice[] = [];

  statusesChoices: Choice[] = [
    {
      id: FacebookLocationStatusEnum.CONNECTED,
      label: this.basicService.translate('add-account-fb/loc/status/connected'),
    },
    {
      id: FacebookLocationStatusEnum.NOT_CONNECTED,
      label: this.basicService.translate('add-account-fb/loc/status/not_connected'),
    },
    { id: FacebookLocationStatusEnum.ERROR, label: this.basicService.translate('add-account-fb/loc/status/error') },
  ];

  operatorsChoices: Choice[] = [
    { id: 2, label: this.basicService.translate('add-account-fb/loc/filters/operators/name/contains') },
    { id: 1, label: this.basicService.translate('add-account-fb/loc/filters/operators/name/equals') },
    { id: 3, label: this.basicService.translate('add-account-fb/loc/filters/operators/name/starts-with') },
    { id: 4, label: this.basicService.translate('add-account-fb/loc/filters/operators/name/ends-with') },
    { id: 0, label: this.basicService.translate('add-account-fb/loc/filters/operators/storeCode/equals') },
  ];

  selectAccountForm = this.formBuilder.group({
    site: [null],
  });

  filtersGroup = new FormGroup({
    search: new FormControl(''),
    operator: new FormControl(this.operatorsChoices[0].id),
    statuses: new FormControl([]),
  });

  displayedColumns = ['checkbox', 'status', 'name', 'link', 'id'];

  dataSource: MatTableDataSource<FacebookLocation> = new MatTableDataSource([]);
  filteredDataNotConnectedLength = 0; // length of data to consider for 'select all' and disabled checkbox
  filtersValues: any; // backup filters values if changing them before getting locations

  selection: SelectionModel<FacebookLocation>;
  readonly MAX_CONNECTIONS_ALLOWED = 1000;
  isAllSelected = false;
  masterCheckboxLabel = '';

  showProgressionDialog = false;
  progressionDialogData: ProgressionDialogData = {
    done: 0,
    total: 0,
    progressionPercentage: 0,
    hasError: false,
    isLoading: false,
    updateProgression: new EventEmitter(),
    title: this.basicService.translate('add-account-fb/loc/progress/title'),
    progressionText: this.basicService.translate('add-account-fb/loc/progress/text/in-progress'),
    finishedText: this.basicService.translate('add-account-fb/loc/progress/text/finished'),
    errorText: this.basicService.translate('add-account-fb/loc/progress/text/error'),
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('progressDialogTplRef') progressDialogTplRef: TemplateRef<any>;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    public dialog: MatDialog,
    private facebookLocationsService: FacebookLocationsService,
    private facebookService: FacebookService,
    private formBuilder: FormBuilder,
    private sitesService: SitesService
  ) {}

  ngOnInit(): void {
    this.facebookSitesChoices = this.sitesService.sitesV2.reduce((acc: Choice[], site) => {
      if (site.type === this.facebookService.getType() && !site.isLocation) {
        acc.push({
          id: site.id,
          label: site.name,
        });
      }
      return acc;
    }, []);

    this.subscriptions.add(
      this.selectAccountForm.get('site').valueChanges.subscribe(value => {
        this.fetchLocations(value);
      })
    );

    this.subscriptions.add(
      this.filtersGroup.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        this.dataSource.filter = value;
        this.filtersValues = value;
        this.updateFilteredDataNotConnectedLength();

        this.selection?.clear();
        this.updateIsAllSelected();
      })
    );

    this.selectAccountForm.get('site').setValue(this.facebookSitesChoices[0].id);
    this.filtersGroup.get('statuses').setValue([FacebookLocationStatusEnum.NOT_CONNECTED]);
  }

  private fetchLocations(siteId: number) {
    this.basicService.showSpinner();
    this.facebookLocationsService
      .fetchLocations(siteId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: results => {
          results.forEach(location => {
            this.getChipColor(location);

            if (!location.link) {
              location.link = 'https://www.facebook.com/' + location.id;
            }
          });

          this.dataSource = new MatTableDataSource(results);
          this.selection = new SelectionModel(true);
          this.setupFilters();

          setTimeout(() => {
            this.dataSource.sort = this.sort;
            this.dataSource.sortingDataAccessor = (item, property) => {
              switch (property) {
                case 'status':
                  return item.connectionStatusText;
                default: {
                  return item[property];
                }
              }
            };

            if (this.filtersValues) {
              this.dataSource.filter = this.filtersValues;
            }

            this.updateFilteredDataNotConnectedLength();
          });
        },
      });
  }

  private getChipColor(location: FacebookLocation) {
    switch (location.connectionStatus) {
      case FacebookLocationStatusEnum.CONNECTED:
        location.chipColor = TableLabelCellEnum.GREEN;
        location.connectionStatusText = this.basicService.translate('add-account-fb/loc/status/connected');
        break;
      case FacebookLocationStatusEnum.CONNECTING:
        location.chipColor = TableLabelCellEnum.YELLOW;
        location.connectionStatusText = this.basicService.translate('add-account-fb/loc/status/connecting');
        break;
      case FacebookLocationStatusEnum.ERROR:
        location.chipColor = TableLabelCellEnum.RED;
        location.connectionStatusText = this.basicService.translate('add-account-fb/loc/status/error');
        break;
      case FacebookLocationStatusEnum.NOT_CONNECTED:
        location.chipColor = TableLabelCellEnum.GREY;
        location.connectionStatusText = this.basicService.translate('add-account-fb/loc/status/not_connected');
        break;
    }
  }

  private setupFilters() {
    this.dataSource.filterPredicate = (location: FacebookLocation, filter) => {
      if (!filter) {
        return true;
      }

      const { search, statuses } = filter as any;
      const locationId = location.id || '';
      const nameLowercase = (location.name || '').toLowerCase();
      const filterLowercase = (search || '').toLowerCase();
      let searchMatched = false;

      switch (this.filtersGroup.value.operator) {
        case 0:
          searchMatched = locationId === filterLowercase;
          break;
        case 1:
          searchMatched = nameLowercase === filterLowercase;
          break;
        case 2:
          searchMatched = nameLowercase.indexOf(filterLowercase) > -1;
          break;
        case 3:
          searchMatched = nameLowercase.startsWith(filterLowercase);
          break;
        case 4:
          searchMatched = nameLowercase.endsWith(filterLowercase);
          break;
        default:
          break;
      }

      return (
        (!search || searchMatched) && (!statuses || !statuses.length || statuses.includes(location.connectionStatus))
      );
    };
  }

  private updateFilteredDataNotConnectedLength() {
    this.filteredDataNotConnectedLength = this.dataSource.filteredData.filter(
      loc => loc.connectionStatus !== FacebookLocationStatusEnum.CONNECTED
    ).length;

    this.masterCheckboxLabel = this.getMasterCheckboxLabel();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  private updateIsAllSelected() {
    this.isAllSelected = false;

    if (this.selection) {
      this.isAllSelected =
        this.filteredDataNotConnectedLength > 0 &&
        (this.selection.selected.length === this.filteredDataNotConnectedLength ||
          this.selection.selected.length === this.MAX_CONNECTIONS_ALLOWED);
    }

    this.masterCheckboxLabel = this.getMasterCheckboxLabel();
  }

  clearSearch() {
    this.filtersGroup.get('search').reset('');
  }

  clearFilters() {
    this.clearSearch();
    this.filtersGroup.get('statuses').reset([]);
    this.filtersValues = null;
  }

  hasFilters() {
    return (
      this.filtersValues &&
      (this.filtersGroup.get('statuses').value.length || this.filtersGroup.get('search').value !== '')
    );
  }

  toggleRow(location: FacebookLocation) {
    this.selection.toggle(location);
    this.updateIsAllSelected();
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   * Do not select 'connected'
   */
  masterToggle() {
    if (this.isAllSelected) {
      this.selection.clear();
      this.updateIsAllSelected();
      return;
    }

    const maxSelection = Math.min(this.MAX_CONNECTIONS_ALLOWED, this.filteredDataNotConnectedLength);

    let i = 0;
    while (this.selection.selected.length < maxSelection && this.dataSource.filteredData[i]) {
      if (
        !this.selection.isSelected(this.dataSource.filteredData[i]) &&
        this.dataSource.filteredData[i].connectionStatus !== FacebookLocationStatusEnum.CONNECTED
      ) {
        this.selection.select(this.dataSource.filteredData[i]);
      }

      i++;
    }

    this.updateIsAllSelected();
  }

  private getMasterCheckboxLabel(): string {
    if (!this.filteredDataNotConnectedLength) {
      return '';
    }

    if (!this.isAllSelected) {
      let text = this.basicService.translate('selector/select-all');

      if (this.filteredDataNotConnectedLength > this.MAX_CONNECTIONS_ALLOWED) {
        text += ` (${this.basicService.translate('max')} ${this.MAX_CONNECTIONS_ALLOWED})`;
      }

      return text;
    }

    return this.basicService.translate('selector/unselect-all');
  }

  getCheckboxLabel(location: FacebookLocation): string {
    if (location.connectionStatus === FacebookLocationStatusEnum.CONNECTED) {
      return this.basicService.translate('add-account-fb/loc/filters/operators/already-connected');
    }

    if (this.selection.selected.length >= this.MAX_CONNECTIONS_ALLOWED && !this.selection.isSelected(location)) {
      return this.basicService.translate('add-account-fb/loc/filters/operators/max-selection', {
        nb: `${this.MAX_CONNECTIONS_ALLOWED}`,
      });
    }

    return '';
  }

  showSiteGroupDialog() {
    const allFbSiteGroups = this.sitesService.siteGroupsV2.filter(sg => sg.type === this.facebookService.getType());

    const siteGroups = allFbSiteGroups.map(({ id, name }) => ({ id, label: name, sortKey: name.toLowerCase() }));

    const dialogRef = this.dialog.open(LinkGroupModalComponent, {
      data: {
        companyId: this.alcmeonContextService.getCompany().id,
        siteGroups,
        siteType: this.facebookService.getType(),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('yes'),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.connect(result.siteGroupId);
      }
    });
  }

  private connect(siteGroupId: number) {
    this.progressionDialogData.progressionPercentage = 0;
    this.progressionDialogData.done = 0;
    this.progressionDialogData.total = this.selection.selected.length;
    this.progressionDialogData.isLoading = true;
    this.progressionDialogData.hasError = false;
    this.showProgressionDialog = true;

    from(this.selection.selected)
      .pipe(
        concatMap(location => {
          location.error = null; // reset error if retry
          this.updateStatus(location, FacebookLocationStatusEnum.CONNECTING);

          const payLoad: FacebookLocationConnection = {
            locationExternalId: location.id,
            parentSiteId: this.selectAccountForm.get('site').value,
            siteGroupId: siteGroupId || null,
          };

          return this.facebookLocationsService.connectLocation(payLoad).pipe(
            tap(() => {
              this.updateStatus(location, FacebookLocationStatusEnum.CONNECTED);

              this.filteredDataNotConnectedLength--;
              this.selection.deselect(location);
              this.updateIsAllSelected();
            }),

            // catchError + return of(null) do not interrupt others calls if error
            catchError(e => {
              this.progressionDialogData.hasError = true;
              this.updateStatus(location, FacebookLocationStatusEnum.ERROR);
              location.error = e.error?.error?.error_message_translation_key || 'facebook/location/error';

              return of(null);
            })
          );
        }),
        finalize(() => {
          this.progressionDialogData.isLoading = false;
          this.sitesService.refreshResolvers();
        })
      )
      .subscribe({
        next: () => {
          this.progressionDialogData.done++;
          this.progressionDialogData.updateProgression.emit();
        },
      });
  }

  closeProgressionDialog() {
    this.showProgressionDialog = false;

    if (this.progressionDialogData.hasError) {
      this.filtersGroup.get('statuses').setValue([FacebookLocationStatusEnum.ERROR]);
    }
  }

  private updateStatus(location: FacebookLocation, status: FacebookLocationStatusEnum) {
    location.connectionStatus = status;
    this.getChipColor(location);
  }

  showStatusError(error: string) {
    this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('m/error'),
        description: this.basicService.translate(error),
        cancelBtnText: this.basicService.translate('close'),
      },
    });
  }

  goBack() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.add-account');
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
